// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { defineComponent } from '@shared/ui-kit/utils/define-component';
import { useContractReview } from './use-contract-review';
import { ScreenLayout } from '@shared/advisory/account-workflow/layout';
import { NavigationControls } from '@shared/advisory/account-workflow/layout';
import { Translation } from '@shared/ui-kit/providers';
import { ScreenHeader } from '@shared/advisory/account-workflow/layout';
import { Card, Typography, Alert, AlertTitle } from '@mui/material';

export const ContractReview = defineComponent({
  displayName: 'ContractReview',
  useSetup: useContractReview,
  View: ({ onNext, proposal, cards, isLoading }) => {
    return (
      <ScreenLayout
        actions={<NavigationControls onNext={onNext} loading={isLoading} />}
      >
        <div className="w-full">
          <Card className="!mb-6 !rounded-lg !p-6" variant="outlined">
            <ScreenHeader
              title={
                <Translation
                  i18nKey={`contract-review-header.title.${
                    proposal?.advisory_type || 'non-independent'
                  }`}
                  ns="components"
                />
              }
              subtitle={
                <Translation i18nKey="contract-review.intro-text" ns="components" />
              }
              className="mb-7"
              data-testid="header"
            />

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {cards.map(card => {
                const { id, labelKey, value, explanationKey } = card;

                return (
                  <div
                    key={id}
                    className="!flex !flex-col !gap-2 !rounded-lg !bg-section !p-6"
                    data-testid={`${id}-card`}
                  >
                    <Typography
                      variant="body1"
                      data-testid={`${id}-label`}
                      className="!font-semibold"
                    >
                      <Translation i18nKey={labelKey as any} ns="components" />
                    </Typography>

                    <Typography variant="body2" data-testid={`${id}-value`}>
                      {value}
                    </Typography>

                    {explanationKey && (
                      <Typography
                        variant="caption"
                        data-testid={`${id}-explanation`}
                        className="!text-gray-500"
                      >
                        <Translation
                          i18nKey={explanationKey as any}
                          ns="components"
                        />
                      </Typography>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>

          <Alert
            severity="info"
            className="!rounded-lg !bg-alert-info-bg text-alert-info-text"
            data-testid="next-step-banner"
          >
            <AlertTitle>
              <Translation
                i18nKey="contract-review.next-step.profiling.title"
                ns="components"
              />
            </AlertTitle>

            <p data-testid="next-step-description">
              <Translation
                i18nKey="contract-review.next-step.profiling.description"
                ns="components"
              />
            </p>
          </Alert>
        </div>
      </ScreenLayout>
    );
  },
});
